import { useEffect, useState } from "react";
import api, { apiError, API } from "../lib/api";
import { PageHeader } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Save, Send, ShieldCheck, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { WhatsAppLiveChat } from "../components/WhatsAppLiveChat";
import { ServiceReminders } from "../components/ServiceReminders";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const META_STATUS_BADGE = {
  approved: "text-green-300 border-green-800 bg-green-950/30",
  pending: "text-amber-300 border-amber-700 bg-amber-950/30",
  rejected: "text-red-300 border-red-800 bg-red-950/30",
  paused: "text-orange-300 border-orange-800 bg-orange-950/30", disabled: "text-red-300 border-red-800 bg-red-950/30",
  not_found: "text-red-300 border-red-800 bg-red-950/30",
  not_created: "text-muted-foreground border-border",
  not_required: "text-muted-foreground border-border",
};
const META_STATUS_LABEL = {
  approved: "Approved", pending: "Pending", rejected: "Rejected", paused: "Paused", disabled: "Disabled",
  in_appeal: "In Appeal", not_found: "Not Found in Meta", not_created: "Not Created", not_required: "Not Required",
};

const NOTIFY_TYPES = [
  ["customer_booking", "Customer Booking Confirmation"],
  ["customer_reminder", "Customer Appointment Reminder"],
  ["customer_cancellation", "Customer Cancellation"],
  ["customer_reschedule", "Customer Rescheduling"],
  ["staff_new_appointment", "Staff New Appointment"],
  ["staff_reminder", "Staff Appointment Reminder"],
  ["staff_cancellation", "Staff Cancellation"],
  ["staff_reschedule", "Staff Rescheduling"],
  ["customer_arrived", "Customer Arrived"],
];

const STATUS_COLOR = {
  sent: "text-green-300 border-green-800", delivered: "text-green-300 border-green-800",
  read: "text-blue-300 border-blue-800", pending: "text-orange-300 border-orange-500/40",
  failed: "text-red-300 border-red-800", restricted: "text-amber-300 border-amber-700", blocked: "text-amber-300 border-amber-700",
};
const STATUS_LABEL = { restricted: "Meta delivery restricted", blocked: "Blocked" };

export default function WhatsAppSettings() {
  const { user } = useAuth();
  const canConfig = ["OWNER", "SUPER_ADMIN"].includes(user?.role) || !!(user?.permissions || {}).whatsapp_access;
  const canChat = ["OWNER", "SUPER_ADMIN"].includes(user?.role) || !!(user?.permissions || {}).whatsapp_chat_view || !!(user?.permissions || {}).whatsapp_chat_send;
  const [cfg, setCfg] = useState({ phone_number_id: "", business_account_id: "", access_token: "", verify_token: "", app_secret: "", notifications: {} });
  const [enabled, setEnabled] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [testMobile, setTestMobile] = useState("");
  const [waStatus, setWaStatus] = useState(null);
  const [tplMobile, setTplMobile] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [apptTestMobile, setApptTestMobile] = useState("");
  const [staffReportId, setStaffReportId] = useState("");
  const [testingJob, setTestingJob] = useState("");

  const loadCfg = () => api.get("/integrations/config").then((r) => {
    const w = r.data.whatsapp || { enabled: false, config: {} };
    setEnabled(w.enabled);
    setCfg({ phone_number_id: "", business_account_id: "", access_token: "", verify_token: "", app_secret: "", notifications: {}, ...w.config });
  });
  const loadTemplates = () => api.get("/whatsapp/templates").then((r) => setTemplates(r.data));
  const loadLogs = () => api.get("/whatsapp/logs").then((r) => setLogs(r.data));
  const loadStatus = () => api.get("/whatsapp/status").then((r) => setWaStatus(r.data)).catch(() => {});
  useEffect(() => { loadCfg(); loadTemplates(); loadLogs(); loadStatus(); api.get("/staff").then((r) => setStaffList(r.data || [])).catch(() => {}); }, []);

  const webhookUrl = `${API}/wa-ai/webhook`;

  const saveCfg = async () => {
    try {
      await api.put("/integrations/config/whatsapp", { enabled, config: cfg });
      toast.success("WhatsApp settings saved"); loadCfg();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleNotif = (key) => setCfg((c) => ({ ...c, notifications: { ...c.notifications, [key]: !(c.notifications?.[key] !== false) } }));

  const saveTemplate = async (key, patch) => {
    try {
      const { data } = await api.put(`/whatsapp/templates/${key}`, patch);
      setTemplates((ts) => ts.map((t) => (t.key === key ? data : t)));
      toast.success("Template updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const testTemplate = async (key) => {
    const mobile = tplMobile[key];
    if (!mobile) { toast.error("Enter a test mobile number"); return; }
    try {
      const { data } = await api.post(`/whatsapp/templates/${key}/test`, { mobile });
      toast[data.status === "sent" ? "success" : "message"](`Test: ${data.status}${data.reason ? " — " + data.reason : ""}`);
      loadLogs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const syncMeta = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/whatsapp/templates/sync");
      if (data.ok) { toast.success(`Synced ${data.synced} template(s) · ${data.found} found in Meta (WABA ${data.waba_id})`); loadTemplates(); }
      else { toast.error(`Sync failed — ${data.reason}`, { duration: 12000 }); loadLogs(); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const [mapping, setMapping] = useState(false);
  const [defs, setDefs] = useState(null);
  const mapMeta = async () => {
    setMapping(true);
    try {
      const { data } = await api.post("/whatsapp/templates/map-meta");
      const ready = (data.report || []).filter((g) => g.ready_to_send_out_of_window).length;
      toast.success(`Mapped ${data.mapped_count} templates · ${ready}/${(data.report || []).length} approved & ready`);
      loadTemplates();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setMapping(false); }
  };
  const showDefs = async () => {
    try {
      const { data } = await api.get("/whatsapp/templates/meta-definitions");
      setDefs(data.definitions || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const sendTest = async () => {
    try { const { data } = await api.post("/whatsapp/test", { mobile: testMobile }); toast[data.status === "sent" ? "success" : "message"](`Test: ${data.status}${data.reason ? " — " + data.reason : ""}`); loadLogs(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const testStaffAppointment = async () => {
    if (!apptTestMobile) { toast.error("Enter a test mobile number"); return; }
    setTestingJob("appt");
    try {
      const { data } = await api.post("/whatsapp/templates/staff_new_appointment/test", { mobile: apptTestMobile });
      toast[data.status === "sent" ? "success" : "message"](`Staff Appointment Notification: ${data.status}${data.reason ? " — " + data.reason : ""}`);
      loadLogs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTestingJob(""); }
  };

  const testOwnerReport = async () => {
    setTestingJob("owner");
    try {
      const { data } = await api.post("/reports/daily-test");
      if (data.ok) toast.success(`Owner Daily Report sent to: ${(data.sent_to || []).join(", ") || "—"}`);
      else toast.message(data.message || "Add at least one enabled owner number in Reports settings.");
      loadLogs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTestingJob(""); }
  };

  const testStaffReport = async () => {
    if (!staffReportId) { toast.error("Select a staff member"); return; }
    setTestingJob("staff");
    try {
      const { data } = await api.post(`/reports/staff/${staffReportId}/test`);
      if (data.ok) toast.success(`Staff Daily Report sent to: ${data.sent_to}`);
      else toast.message(data.message || "Could not send staff report.");
      loadLogs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTestingJob(""); }
  };

  return (
    <div>
      <PageHeader title="WhatsApp Integration" subtitle="Central Define Salon WhatsApp Business — appointment notifications" />

      {waStatus && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="wa-status-badges">
          <Badge variant="outline" className={`gap-1 ${waStatus.api_connected ? "text-green-300 border-green-800 bg-green-950/30" : "text-red-300 border-red-800 bg-red-950/30"}`} data-testid="wa-status-api">
            {waStatus.api_connected ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
            WhatsApp API: {waStatus.api_connected ? "Connected" : "Not Connected"}
            {waStatus.api_connected && waStatus.credential_source && <span className="opacity-60 ml-1">({waStatus.credential_source})</span>}
          </Badge>
          <Badge variant="outline" className={`gap-1 ${waStatus.notifications_ready ? "text-green-300 border-green-800 bg-green-950/30" : "text-amber-300 border-amber-700 bg-amber-950/30"}`} data-testid="wa-status-notif">
            {waStatus.notifications_ready ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
            Automated Notifications: {waStatus.notifications_ready ? "Ready" : "Not Ready"}
          </Badge>
        </div>
      )}

      <Tabs defaultValue={canConfig ? "config" : "livechat"}>
        <TabsList className="bg-secondary">
          {canConfig && <TabsTrigger value="config" data-testid="wa-tab-config">Configuration</TabsTrigger>}
          {canConfig && <TabsTrigger value="notifications" data-testid="wa-tab-notif">Notifications</TabsTrigger>}
          {canConfig && <TabsTrigger value="templates" data-testid="wa-tab-templates">Templates</TabsTrigger>}
          {canConfig && <TabsTrigger value="logs" data-testid="wa-tab-logs">Message Logs</TabsTrigger>}
          {canConfig && <TabsTrigger value="reminders" data-testid="wa-tab-reminders">Service Due Reminders</TabsTrigger>}
          {canChat && <TabsTrigger value="livechat" data-testid="wa-tab-livechat">WhatsApp Live Chat</TabsTrigger>}
        </TabsList>

        <TabsContent value="reminders"><ServiceReminders isOwner={["OWNER", "SUPER_ADMIN"].includes(user?.role)} /></TabsContent>

        <TabsContent value="config">
          <Card className="p-6 bg-card border-border max-w-2xl space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><ShieldCheck size={16} className="text-primary" /> Secrets are stored server-side and shown masked. Status: <Badge variant="outline" className={enabled ? "text-green-300 border-green-800" : "text-muted-foreground"}>{enabled ? "Enabled" : "Disabled"}</Badge></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Phone Number ID</Label><Input value={cfg.phone_number_id} onChange={(e) => setCfg({ ...cfg, phone_number_id: e.target.value })} className="bg-secondary mt-1" data-testid="wa-phone-id" /></div>
              <div><Label className="text-xs">Business Account ID</Label><Input value={cfg.business_account_id} onChange={(e) => setCfg({ ...cfg, business_account_id: e.target.value })} className="bg-secondary mt-1" data-testid="wa-business-id" /></div>
            </div>
            <div><Label className="text-xs">Access Token (secret)</Label><Input type="password" placeholder="••••••" value={cfg.access_token} onChange={(e) => setCfg({ ...cfg, access_token: e.target.value })} className="bg-secondary mt-1" data-testid="wa-token" /></div>
            <div><Label className="text-xs">Webhook Verify Token</Label><Input value={cfg.verify_token} onChange={(e) => setCfg({ ...cfg, verify_token: e.target.value })} className="bg-secondary mt-1" data-testid="wa-verify-token" /></div>
            <div><Label className="text-xs">App Secret (secret)</Label><Input type="password" placeholder="••••••" value={cfg.app_secret} onChange={(e) => setCfg({ ...cfg, app_secret: e.target.value })} className="bg-secondary mt-1" data-testid="wa-app-secret" /><p className="text-[11px] text-muted-foreground mt-1">Meta App → Settings → Basic → App Secret. Used to verify inbound webhook signatures. Optional but recommended.</p></div>
            <div><Label className="text-xs">Webhook URL (add this in Meta)</Label><Input readOnly value={webhookUrl} className="bg-secondary mt-1 text-muted-foreground" data-testid="wa-webhook-url" /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="wa-enabled" /> Enable WhatsApp sending
            </label>
            <div className="flex gap-2">
              <Button onClick={saveCfg} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="wa-save-config"><Save size={15} className="mr-1" /> Save Settings</Button>
            </div>
            <div className="border-t border-border pt-4 flex gap-2 items-end">
              <div className="flex-1"><Label className="text-xs">Send a test message to</Label><Input value={testMobile} onChange={(e) => setTestMobile(e.target.value)} placeholder="Mobile number" className="bg-secondary mt-1" data-testid="wa-test-mobile" /></div>
              <Button variant="outline" onClick={sendTest} className="rounded-sm" data-testid="wa-test-send"><Send size={15} className="mr-1" /> Send Test</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card className="p-6 bg-card border-border max-w-xl">
            <p className="text-sm text-muted-foreground mb-4">Turn each notification type on/off. Changes save with the button below.</p>
            <div className="space-y-2">
              {NOTIFY_TYPES.map(([key, label]) => (
                <label key={key} className="flex items-center justify-between bg-secondary rounded-sm px-3 py-2 text-sm cursor-pointer" data-testid={`wa-notif-${key}`}>
                  {label}
                  <input type="checkbox" checked={cfg.notifications?.[key] !== false} onChange={() => toggleNotif(key)} />
                </label>
              ))}
            </div>
            <Button onClick={saveCfg} className="mt-4 rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="wa-save-notif"><Save size={15} className="mr-1" /> Save Notification Settings</Button>
          </Card>

          <Card className="p-6 bg-card border-border max-w-xl mt-4" data-testid="wa-notif-tests">
            <p className="font-medium text-sm mb-1">Send Test Now</p>
            <p className="text-[11px] text-muted-foreground mb-4">Each test uses the SAME production sending pipeline as the automatic scheduler, and is recorded in Message Logs with its status &amp; any failure reason.</p>

            <div className="space-y-4">
              <div>
                <Label className="text-xs">Staff Appointment Notification</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={apptTestMobile} onChange={(e) => setApptTestMobile(e.target.value)} placeholder="Staff mobile number" className="bg-secondary" data-testid="wa-test-appt-mobile" />
                  <Button variant="outline" onClick={testStaffAppointment} disabled={testingJob === "appt"} className="rounded-sm whitespace-nowrap" data-testid="wa-test-appt-send"><Send size={14} className="mr-1" /> {testingJob === "appt" ? "…" : "Send Test"}</Button>
                </div>
              </div>

              <div>
                <Label className="text-xs">Owner Daily Report</Label>
                <div className="flex gap-2 mt-1">
                  <p className="flex-1 text-[11px] text-muted-foreground self-center">Sends to all enabled owner numbers configured in Reports settings.</p>
                  <Button variant="outline" onClick={testOwnerReport} disabled={testingJob === "owner"} className="rounded-sm whitespace-nowrap" data-testid="wa-test-owner-report"><Send size={14} className="mr-1" /> {testingJob === "owner" ? "…" : "Send Test"}</Button>
                </div>
              </div>

              <div>
                <Label className="text-xs">Staff Daily Report</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={staffReportId} onValueChange={setStaffReportId}>
                    <SelectTrigger className="bg-secondary" data-testid="wa-test-staff-report-select"><SelectValue placeholder="Select staff member" /></SelectTrigger>
                    <SelectContent>
                      {staffList.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={testStaffReport} disabled={testingJob === "staff"} className="rounded-sm whitespace-nowrap" data-testid="wa-test-staff-report-send"><Send size={14} className="mr-1" /> {testingJob === "staff" ? "…" : "Send Test"}</Button>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <div className="flex items-center justify-between mb-3 max-w-4xl gap-2 flex-wrap">
            <p className="text-sm text-muted-foreground">Manage every automated message. Toggle on/off, choose the source, and test each one.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={showDefs} className="rounded-sm" data-testid="wa-show-defs">
                Meta templates to create
              </Button>
              <Button onClick={mapMeta} disabled={mapping} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="wa-map-meta">
                {mapping ? "Mapping…" : "Map all to Meta"}
              </Button>
              <Button variant="outline" onClick={syncMeta} disabled={syncing} className="rounded-sm" data-testid="wa-sync-meta">
                <RefreshCw size={14} className={`mr-1 ${syncing ? "animate-spin" : ""}`} /> Sync with Meta
              </Button>
            </div>
          </div>
          {defs && (
            <Card className="p-4 bg-card border-primary/40 max-w-4xl mb-3" data-testid="wa-defs-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-sm">Create these {defs.length} templates in WhatsApp Manager → Message Templates</p>
                <Button size="sm" variant="ghost" onClick={() => setDefs(null)} data-testid="wa-defs-close">Close</Button>
              </div>
              <div className="grid gap-2 max-h-[420px] overflow-y-auto">
                {defs.map((d) => (
                  <div key={d.key} className="border border-border rounded-sm p-2 text-xs" data-testid={`wa-def-${d.key}`}>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mb-1">
                      <span className="text-muted-foreground">{d.notification}</span>
                      <span>Name: <span className="font-mono text-primary">{d.meta_name}</span></span>
                      <span>Lang: <span className="font-mono">{d.language}</span></span>
                      <span>Category: <span className="font-mono">{d.category}</span></span>
                    </div>
                    <pre className="whitespace-pre-wrap bg-secondary p-2 rounded-sm text-[11px]">{d.body}</pre>
                    <p className="text-[10px] text-muted-foreground mt-1">Body variables in order: {d.variables_in_order.map((v, i) => `{{${i + 1}}}=${v}`).join("  ·  ")}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
          <div className="grid gap-3 max-w-4xl">
            {templates.map((t) => (
              <Card key={t.key} className="p-4 bg-card border-border" data-testid={`wa-template-${t.key}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div>
                    <p className="font-medium text-sm">{t.label}</p>
                    <p className="text-[11px] text-muted-foreground font-mono">{t.key}</p>
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <span className={t.enabled ? "text-green-400" : "text-muted-foreground"}>{t.enabled ? "Enabled" : "Disabled"}</span>
                    <Switch checked={t.enabled} onCheckedChange={(v) => saveTemplate(t.key, { enabled: v })} data-testid={`wa-tpl-enabled-${t.key}`} />
                  </label>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 mb-3">
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Template Source</Label>
                    <Select value={t.source} onValueChange={(v) => saveTemplate(t.key, { source: v })}>
                      <SelectTrigger className="bg-secondary mt-1 h-9 text-sm" data-testid={`wa-tpl-source-${t.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">Internal Message</SelectItem>
                        <SelectItem value="meta_template">Meta WhatsApp Template</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Meta Template Status</Label>
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap"><Badge variant="outline" className={`text-xs ${META_STATUS_BADGE[t.meta_status] || ""}`} data-testid={`wa-tpl-metastatus-${t.key}`}>{META_STATUS_LABEL[t.meta_status] || (t.meta_status_raw || t.meta_status || "").toString().toUpperCase()}</Badge>
                      {t.meta_category && <span className="text-[10px] text-muted-foreground uppercase" data-testid={`wa-tpl-metacategory-${t.key}`}>{t.meta_category}</span>}
                      {t.meta_language_synced && <span className="text-[10px] text-muted-foreground" data-testid={`wa-tpl-metalang-${t.key}`}>· {t.meta_language_synced}</span>}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Send Method</Label>
                    <p className="text-sm mt-1.5" data-testid={`wa-tpl-method-${t.key}`}>{t.send_method === "approved_template" ? "Approved Template Message" : "Session Message"}</p>
                  </div>
                </div>

                {t.source === "meta_template" && (
                  <div className="grid sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Meta Template Name</Label>
                      <Input defaultValue={t.meta_name} placeholder="approved_template_name" className="bg-secondary mt-1 h-9 text-sm" data-testid={`wa-tpl-metaname-${t.key}`}
                        onBlur={(e) => { if (e.target.value !== t.meta_name) saveTemplate(t.key, { meta_name: e.target.value }); }} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Language</Label>
                      <Input defaultValue={t.meta_language} placeholder="en" className="bg-secondary mt-1 h-9 text-sm" data-testid={`wa-tpl-metalang-${t.key}`}
                        onBlur={(e) => { if (e.target.value !== t.meta_language) saveTemplate(t.key, { meta_language: e.target.value }); }} />
                    </div>
                    {t.meta_variables?.length > 0 && (
                      <div className="sm:col-span-2">
                        <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Body variables (order Meta {"{{1}}…{{n}}"} must follow)</Label>
                        <p className="text-[11px] mt-1 font-mono text-muted-foreground" data-testid={`wa-tpl-vars-${t.key}`}>
                          {t.meta_variables.map((v, i) => `{{${i + 1}}}=${v}`).join("  ·  ")}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Message Body {t.source === "meta_template" && "(used for internal fallback / preview)"}</Label>
                <Textarea defaultValue={t.body} rows={3} className="bg-secondary text-sm mt-1"
                  onBlur={(e) => { if (e.target.value !== t.body) saveTemplate(t.key, { body: e.target.value }); }} data-testid={`wa-template-body-${t.key}`} />
                <p className="text-[11px] text-muted-foreground mt-1">Variables: {"{{customer_name}} {{staff_name}} {{service_name}} {{branch_name}} {{appointment_date}} {{appointment_time}} {{customer_mobile}} {{invoice_no}} {{amount}} {{invoice_url}}"}</p>

                <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-border">
                  <p className="text-[11px] text-muted-foreground" data-testid={`wa-tpl-synced-${t.key}`}>Last synced with Meta: {t.last_synced_at ? new Date(t.last_synced_at).toLocaleString() : "Never"}</p>
                  <div className="flex gap-2 items-center">
                    <Input value={tplMobile[t.key] || ""} onChange={(e) => setTplMobile((m) => ({ ...m, [t.key]: e.target.value }))} placeholder="Test mobile" className="bg-secondary h-9 text-sm w-40" data-testid={`wa-tpl-testmobile-${t.key}`} />
                    <Button variant="outline" size="sm" onClick={() => testTemplate(t.key)} className="rounded-sm" data-testid={`wa-tpl-testsend-${t.key}`}><Send size={13} className="mr-1" /> Test Send</Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="logs">
          <Card className="bg-card border-border overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="border-border hover:bg-transparent">
                <TableHead>Time</TableHead><TableHead>To</TableHead><TableHead>Audience</TableHead>
                <TableHead>Type</TableHead><TableHead>Branch</TableHead><TableHead>Template · Meta ID</TableHead><TableHead>Status</TableHead><TableHead>Reason</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.id} className="border-border" data-testid={`wa-log-${l.id}`}>
                    <TableCell className="text-xs text-muted-foreground">{(l.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="font-mono text-xs">{l.recipient}</TableCell>
                    <TableCell className="text-xs">{l.audience}</TableCell>
                    <TableCell className="text-xs">{l.type}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{l.branch_name || "—"}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground max-w-[200px]" data-testid={`wa-log-meta-${l.id}`}>
                      <div className="truncate">{l.template || l.meta_template_name || (l.mode === "session" ? "session text" : "—")}{l.trigger ? ` · ${l.trigger}` : ""}</div>
                      {l.provider_id && <div className="font-mono truncate" title={l.provider_id}>{l.provider_id}</div>}
                    </TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_COLOR[l.status] || ""} data-testid={`wa-log-status-${l.id || l._id}`}>{STATUS_LABEL[l.status] || l.status}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[260px]" title={[l.reason, l.meta_error && `code ${l.meta_error.code}${l.meta_error.subcode ? "." + l.meta_error.subcode : ""}`, l.meta_error?.message].filter(Boolean).join(" | ")}>
                      <div className="truncate">{l.reason || "—"}</div>
                      {(l.meta_error_code || l.meta_error?.code) && <div className="text-[10px] text-red-300">Meta #{l.meta_error_code || l.meta_error.code}{(l.meta_error_subcode || l.meta_error?.subcode) ? `.${l.meta_error_subcode || l.meta_error.subcode}` : ""}</div>}
                    </TableCell>
                  </TableRow>
                ))}
                {!logs.length && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No messages yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {canChat && (
          <TabsContent value="livechat" data-testid="wa-livechat-section">
            <WhatsAppLiveChat />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
